import React, { useState, useEffect, useCallback } from 'react';
import { CalendarRange, Scale, Check, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { configService } from '../../services/configService';

// =============================================================================
// Purchase → Budget Setup
// Chooses how procurement pricing rolls up for the active budget version:
//   actual_monthly   — keep the price captured per month (forecasting).
//   weighted_average — collapse to one weighted-average yearly price (budget;
//                      a single price loaded into the ERP).
// Stored in admin_config (module MOD_2_SUPPLY, item pricing_method), version-wide.
// =============================================================================

const MODULE = 'MOD_2_SUPPLY';
const ITEM = 'pricing_method';
const DEFAULT_METHOD = 'weighted_average';

const OPTIONS = [
  {
    value: 'actual_monthly',
    icon: CalendarRange,
    title: 'Actual pricing (by month)',
    blurb: 'Keep each period’s price as captured. Prices flow month-by-month — typically used for forecasting.',
  },
  {
    value: 'weighted_average',
    icon: Scale,
    title: 'Weighted-average price (yearly)',
    blurb: 'Collapse the year to a single weighted-average price — typically used for budgets, where only one price is loaded into the ERP.',
  },
];

const PurchaseBudgetSetup = () => {
  const { selectedVersion } = useAppContext();

  const [method, setMethod] = useState(DEFAULT_METHOD);
  const [loading, setLoading] = useState(true);
  const [savingValue, setSavingValue] = useState(null); // the value currently being saved
  const [savedAt, setSavedAt] = useState(0);            // bump to flash "Saved"
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!selectedVersion) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const r = await configService.getModuleConfig(MODULE, ITEM);
      if (r?.success) setMethod(r.data.value || DEFAULT_METHOD);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load setting.');
    } finally {
      setLoading(false);
    }
  }, [selectedVersion]);

  useEffect(() => { load(); }, [load]);

  const choose = async (value) => {
    if (value === method || savingValue) return;
    const previous = method;
    setMethod(value);          // optimistic
    setSavingValue(value);
    setError('');
    try {
      const r = await configService.setModuleConfig(MODULE, ITEM, value);
      if (r?.success) {
        setSavedAt(Date.now());
      } else {
        throw new Error(r?.message || 'Save failed.');
      }
    } catch (e) {
      setMethod(previous);     // roll back
      setError(e.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSavingValue(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Budget Setup</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Choose how procurement pricing is rolled up for the active budget version.
          </p>
        </div>

        {!selectedVersion ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center text-sm text-slate-400">
            Select a budget version in the top bar to configure its pricing method.
          </div>
        ) : loading ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Pricing method</span>
              {savedAt > 0 && !savingValue && !error && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                  <Check className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {OPTIONS.map((opt) => {
                const active = method === opt.value;
                const busy = savingValue === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => choose(opt.value)}
                    disabled={!!savingValue}
                    className={`text-left rounded-xl border p-4 transition-colors disabled:cursor-wait ${
                      active
                        ? 'border-emerald-500 dark:border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/40'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${
                        active ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </span>
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      ) : active ? (
                        <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      ) : null}
                    </div>
                    <h3 className="m-0 mt-3 text-sm font-semibold text-slate-800 dark:text-slate-100">{opt.title}</h3>
                    <p className="m-0 mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{opt.blurb}</p>
                  </button>
                );
              })}
            </div>

            <p className="mt-4 text-xs text-slate-400">
              Applies to this budget version. Changes save immediately.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseBudgetSetup;
